import isEqual from "lodash/isEqual";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import assets from "../../common/assets";
import { genericErrorConfig } from "../../common/exceptions";
import { unitsToListOptions } from "../../common/helpers/listOptions";
import { canManageAdministrators } from "../../common/helpers/municipalityRoles";
import { space } from "../../common/variables";
import Button from "../../components/Button";
import Container from "../../components/Container";
import Cell, { CellHeader, CellHeading } from "../../components/Grid/Cell";
import CellBlock, {
    CellBlockFooter,
    CellBlockSection,
    CellBlockTitle,
    CellBlockValue,
} from "../../components/Grid/CellBlock";
import Grid from "../../components/Grid/Grid";
import Loader from "../../components/Loader";
import { ModalContext } from "../../components/Modal";
import OptionsList from "../../components/OptionsList";
import Plate from "../../components/Plate";
import SubHeader from "../../components/SubHeader";
import Tag from "../../components/Tag";
import TriggerFormInitially from "../../components/TriggerFormInitially";
import { AuthenticatedUserContext } from "../../contexts/AuthenticatedUserContext";
import { useSelectedUnits } from "../../hooks/useSelectedUnits";
import useSelectedMunicipalityId from "../../hooks/useSelectedMunicipalityId";
import useUnsavedChangesModal from "../../hooks/useUnsavedChangesModal";
import { useChangeAdministratorRoleMutation } from "../../mutations/useChangeAdministratorRoleMutation";
import { useDeleteAdministratorMutation } from "../../mutations/useDeleteAdministratorMutation";
import { useUpdateAdministratorMutation } from "../../mutations/useUpdateAdministratorMutation";
import { useAllUnitsQuery } from "../../queries/units/useAllUnitsQuery";
import { useAdministratorByIdQuery } from "../../queries/useAdministratorByIdQuery";
import { routes } from "../../routes";
import { applyHybrisUnitsAndFormErrors } from "../CustomerView/customerDetailsConfig";
import { sortUnits } from "../OrdererView/OrdererView.utils";
import {
    AdministratorInfoFormData,
    useAdministratorInfoForm,
} from "./AdministratorDetailsView.hooks";
import AdministratorInfoForm from "./AdministratorInfoForm";
import AdministratorInfoListing from "./AdministratorInfoListing";
import {
    ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE,
    convertAdministratorForDemote,
    convertAdministratorForUpdate,
    convertAdministratorFromOutput,
    getChangeAdministratorRoleErrorSubtitle,
    getModalAdministratorErrorText,
    mapHybrisAdministratorErrorField,
} from "./administratorDetailsConfig";
import { mapAdministratorsForListing } from "./administratorListingUtils";
import { AdministratorUpdateInput } from "./administratorTypes";

interface AdministratorFormsEditing {
    information?: boolean;
    units?: boolean;
}

const isAdministratorProfileChanged = (
    data: AdministratorInfoFormData,
    original: ReturnType<typeof convertAdministratorFromOutput>,
) =>
    data.firstName !== original.firstName ||
    data.lastName !== original.lastName ||
    data.phone !== original.phone ||
    data.email !== original.email ||
    data.customerCreationAllowed !== original.customerCreationAllowed;

const EditAdministratorView = () => {
    const navigate = useNavigate();
    const municipalityId = useSelectedMunicipalityId();

    const { authUserData } = useContext(AuthenticatedUserContext);
    const { setModalOptions } = useContext(ModalContext);
    const { administratorId } = useParams<{ administratorId: string }>();

    const [isEditing, setIsEditing] = useState<AdministratorFormsEditing | undefined>({
        information: false,
        units: false,
    });
    const profileSavedBeforeRoleChange = useRef(false);
    /** Skip leave-edit on profile PUT success when a /role call will follow. */
    const keepEditingForRoleChange = useRef(false);
    /** Keep pending role radio across profile refetch during chained demote. */
    const pendingRoleDuringChange = useRef<AdministratorInfoFormData["municipalityRole"] | null>(
        null,
    );

    const administratorQuery = useAdministratorByIdQuery(administratorId, {
        enabled: true,
    });

    const storedRole = administratorQuery.data?.municipalityRole;
    const isOrdererAdmin = storedRole === "ORDERER_ADMIN";
    const showDemoteRoleSelect =
        !!authUserData &&
        canManageAdministrators(authUserData.municipalityRole) &&
        isOrdererAdmin;

    const allUnitsQuery = useAllUnitsQuery();
    const allUnitsOptions = useMemo(
        () => unitsToListOptions(allUnitsQuery.data),
        [allUnitsQuery.data],
    );

    const administratorAuthorizedUnits = useMemo(
        () =>
            (administratorQuery.data &&
                allUnitsQuery.data &&
                mapAdministratorsForListing([administratorQuery.data], allUnitsQuery.data)[0]
                    ?.authUnits) ||
            [],
        [allUnitsQuery.data, administratorQuery.data],
    );

    const { selectedUnits, setSelectedUnits, unitsError, setUnitsError, updateUnitList } =
        useSelectedUnits(administratorAuthorizedUnits);

    useEffect(() => {
        if (administratorAuthorizedUnits) {
            setSelectedUnits(administratorAuthorizedUnits);
        }
    }, [administratorAuthorizedUnits, setSelectedUnits]);

    const resetSelectedUnits = () => {
        setSelectedUnits(administratorAuthorizedUnits);
        setUnitsError(undefined);
    };

    const form = useAdministratorInfoForm();

    const deleteAdministratorMutation = useDeleteAdministratorMutation({
        onSuccess: () => {
            setModalOptions({ visible: false });
            navigate(-1);
        },
    });

    const updateAdministratorMutation = useUpdateAdministratorMutation({
        onSuccess: () => {
            if (keepEditingForRoleChange.current) {
                keepEditingForRoleChange.current = false;
                return;
            }

            setIsEditing(undefined);
            setUnitsError(undefined);
        },
        onError: (error) => {
            keepEditingForRoleChange.current = false;
            pendingRoleDuringChange.current = null;

            setModalOptions(
                genericErrorConfig(
                    () => setModalOptions({ visible: false }),
                    "Kunde ej spara administratör.",
                    getModalAdministratorErrorText(error),
                ),
            );

            applyHybrisUnitsAndFormErrors(
                error,
                mapHybrisAdministratorErrorField,
                (fieldName, fieldError) => form.setError(fieldName as any, fieldError),
                setUnitsError,
            );
        },
    });

    const changeAdministratorRoleMutation = useChangeAdministratorRoleMutation({
        onSuccess: () => {
            profileSavedBeforeRoleChange.current = false;
            pendingRoleDuringChange.current = null;
            setModalOptions({ visible: false });
            navigate(routes.orderers(municipalityId));
        },
        onError: (error) => {
            const afterProfileSave = profileSavedBeforeRoleChange.current;
            profileSavedBeforeRoleChange.current = false;
            pendingRoleDuringChange.current = null;
            const roleSubtitle = getChangeAdministratorRoleErrorSubtitle(error);
            const subtitle = afterProfileSave
                ? ["Profiluppgifterna sparades, men rollen kunde inte ändras.", roleSubtitle]
                      .filter(Boolean)
                      .join(" ")
                : roleSubtitle;

            setModalOptions(
                genericErrorConfig(
                    () => setModalOptions({ visible: false }),
                    "Kunde ej ändra roll.",
                    subtitle,
                ),
            );

            applyHybrisUnitsAndFormErrors(
                error,
                mapHybrisAdministratorErrorField,
                (fieldName, fieldError) => form.setError(fieldName as any, fieldError),
                setUnitsError,
            );
        },
    });

    const updateAdministrator = (input: AdministratorUpdateInput) => {
        updateAdministratorMutation.mutate({
            administratorId: administratorId,
            input,
        });
    };

    const demoteAdministrator = () => {
        changeAdministratorRoleMutation.mutate({
            administratorId,
            input: convertAdministratorForDemote(),
        });
    };

    const showDemoteConfirmModal = (data: AdministratorInfoFormData) => {
        setModalOptions({
            visible: true,
            title: "Ändra till Beställare?",
            subtitle: `Är du säker på att du vill ta bort Beställare administratör-behörigheten från ${administratorQuery.data?.firstName} ${administratorQuery.data?.lastName}? Personen flyttas till Beställare och behåller sina enheter.`,
            buttons: [
                <Button
                    size="small"
                    title="Bekräfta"
                    disabled={
                        changeAdministratorRoleMutation.isPending ||
                        updateAdministratorMutation.isPending
                    }
                    onClick={() => {
                        if (
                            changeAdministratorRoleMutation.isPending ||
                            updateAdministratorMutation.isPending ||
                            !storedRole
                        ) {
                            return;
                        }

                        pendingRoleDuringChange.current = data.municipalityRole;

                        const original = convertAdministratorFromOutput(
                            administratorQuery.data,
                        );

                        if (isAdministratorProfileChanged(data, original)) {
                            keepEditingForRoleChange.current = true;
                            updateAdministratorMutation.mutate(
                                {
                                    administratorId,
                                    input: convertAdministratorForUpdate(
                                        data,
                                        selectedUnits,
                                        storedRole,
                                    ),
                                },
                                {
                                    onSuccess: () => {
                                        profileSavedBeforeRoleChange.current = true;
                                        demoteAdministrator();
                                    },
                                },
                            );
                            return;
                        }

                        profileSavedBeforeRoleChange.current = false;
                        demoteAdministrator();
                    }}
                />,
                <Button
                    size="small"
                    title="Avbryt"
                    theme="inverted"
                    disabled={
                        changeAdministratorRoleMutation.isPending ||
                        updateAdministratorMutation.isPending
                    }
                    onClick={() => setModalOptions({ visible: false })}
                />,
            ],
        });
    };

    const showDeletionConfirmModal = () => {
        setModalOptions({
            visible: true,
            title: `Ta bort administratör?`,
            subtitle: `Är du säker på att du vill ta bort ${administratorQuery.data?.firstName} ${administratorQuery.data?.lastName}?`,
            buttons: [
                <Button
                    size="small"
                    title="Ta bort"
                    theme="red"
                    disabled={deleteAdministratorMutation.isPending}
                    onClick={() => {
                        if (deleteAdministratorMutation.isPending) {
                            return;
                        }

                        deleteAdministratorMutation.mutate({ administratorId });
                    }}
                />,
                <Button
                    size="small"
                    title="Avbryt"
                    theme="inverted"
                    onClick={() => setModalOptions({ visible: false })}
                />,
            ],
        });
    };

    useEffect(() => {
        if (administratorQuery.isSuccess) {
            const converted = convertAdministratorFromOutput(administratorQuery.data);
            const pendingRole = pendingRoleDuringChange.current;
            form.reset(
                pendingRole
                    ? { ...converted, municipalityRole: pendingRole }
                    : converted,
            );
            void form.trigger();
        }
    }, [form, administratorQuery.data, administratorQuery.isSuccess]);

    const resetForm = () => {
        form.reset(convertAdministratorFromOutput(administratorQuery.data));
        void form.trigger();
    };

    const selectedUnitsHasChanged = !isEqual(
        sortUnits(selectedUnits),
        sortUnits(administratorAuthorizedUnits),
    );

    const showUnsavedChangesModal = useUnsavedChangesModal();

    const toggleEditMode = (directToggleTo?: keyof AdministratorFormsEditing) => {
        if (!directToggleTo) {
            resetForm();
            resetSelectedUnits();
            setIsEditing(undefined);
            return;
        }

        if (form.formState.isDirty || selectedUnitsHasChanged) {
            showUnsavedChangesModal(() => {
                resetForm();
                resetSelectedUnits();
                setIsEditing({
                    [directToggleTo]: true,
                });
            });
        } else {
            setIsEditing({
                [directToggleTo]: true,
            });
        }
    };

    const isMutationLoading =
        deleteAdministratorMutation.isPending ||
        updateAdministratorMutation.isPending ||
        changeAdministratorRoleMutation.isPending;

    useEffect(() => {
        if (!deleteAdministratorMutation.isPending) {
            return;
        }

        setModalOptions((current) => {
            if (!current.visible) {
                return current;
            }

            return {
                ...current,
                buttons: [
                    <Button size="small" title="Ta bort" theme="red" disabled />,
                    <Button
                        size="small"
                        title="Avbryt"
                        theme="inverted"
                        onClick={() => setModalOptions({ visible: false })}
                    />,
                ],
            };
        });
    }, [deleteAdministratorMutation.isPending, setModalOptions]);

    useEffect(() => {
        const isRoleChangePending =
            changeAdministratorRoleMutation.isPending ||
            updateAdministratorMutation.isPending;
        if (!isRoleChangePending) {
            return;
        }

        setModalOptions((current) => {
            if (!current.visible || current.title !== "Ändra till Beställare?") {
                return current;
            }

            return {
                ...current,
                buttons: [
                    <Button size="small" title="Bekräfta" disabled />,
                    <Button size="small" title="Avbryt" theme="inverted" disabled />,
                ],
            };
        });
    }, [
        changeAdministratorRoleMutation.isPending,
        updateAdministratorMutation.isPending,
        setModalOptions,
    ]);

    const isCurrentUser =
        !!authUserData &&
        !!administratorQuery.data &&
        authUserData.uid === administratorQuery.data.uid;

    const saveWithUnits = (data = convertAdministratorFromOutput(administratorQuery.data)) => {
        if (!storedRole) {
            return;
        }

        if (storedRole === "ORDERER_ADMIN" && selectedUnits.length === 0) {
            setUnitsError(ORDERER_ADMIN_UNITS_REQUIRED_MESSAGE);
            return;
        }

        updateAdministrator(convertAdministratorForUpdate(data, selectedUnits, storedRole));
    };

    const submitInformation = (data: AdministratorInfoFormData) => {
        if (showDemoteRoleSelect && data.municipalityRole === "ORDERER") {
            showDemoteConfirmModal(data);
            return;
        }

        saveWithUnits(data);
    };

    return (
        <Container>
            <SubHeader
                backArrow
                title={`${administratorQuery.data?.firstName} ${administratorQuery.data?.lastName}`}
            />
            <Plate padding="large" spaceBottom={true}>
                {administratorQuery.isLoading ? (
                    <Loader />
                ) : (
                    <Grid gutter={space.padding}>
                        <Cell gutter={space.padding}>
                            <CellHeading>Personuppgifter</CellHeading>
                            <CellBlock
                                active={isEditing?.information}
                                noOutlines={false}
                                onEdit={() => toggleEditMode("information")}>
                                {isEditing?.information ? (
                                    <>
                                        <CellBlockSection>
                                            <CellBlockTitle>
                                                Ändra personuppgifter
                                            </CellBlockTitle>
                                        </CellBlockSection>
                                        <CellBlockSection>
                                            <AdministratorInfoForm
                                                onCancel={() => toggleEditMode(undefined)}
                                                {...form}
                                                isSubmitLoading={isMutationLoading}
                                                isNewForm={false}
                                                showDemoteRoleSelect={showDemoteRoleSelect}
                                                showCustomerCreationAllowed={isOrdererAdmin}
                                                onSubmit={submitInformation}
                                            />
                                            <TriggerFormInitially trigger={form.trigger} />
                                        </CellBlockSection>
                                    </>
                                ) : (
                                    <AdministratorInfoListing
                                        data={form.getValues()}
                                        isValid={form.formState.isValid}
                                        showCustomerCreationAllowed={isOrdererAdmin}
                                    />
                                )}
                            </CellBlock>
                        </Cell>
                        {isOrdererAdmin && (
                            <Cell gutter={space.padding}>
                                <CellHeader>
                                    <CellHeading>
                                        Enheter
                                        {selectedUnits?.length > 0 &&
                                            ` (${selectedUnits.length})`}
                                    </CellHeading>
                                    {isEditing?.units && selectedUnits.length > 0 && (
                                        <div>
                                            <Tag>
                                                {selectedUnits.length === 1
                                                    ? `1 vald enhet`
                                                    : `${selectedUnits.length} valda enheter`}
                                            </Tag>
                                        </div>
                                    )}
                                </CellHeader>
                                {allUnitsOptions?.length || isEditing?.units ? (
                                    <CellBlock
                                        onEdit={() => toggleEditMode("units")}
                                        active={isEditing?.units}>
                                        {!!allUnitsQuery.data?.length && isEditing?.units ? (
                                            <>
                                                <CellBlockSection>
                                                    <CellBlockTitle>
                                                        Ändra enheter
                                                    </CellBlockTitle>
                                                </CellBlockSection>
                                                <CellBlockSection>
                                                    <OptionsList
                                                        overflowValue={space.medium}
                                                        setChoice={updateUnitList}
                                                        data={allUnitsOptions}
                                                        selectedData={selectedUnits}
                                                    />
                                                </CellBlockSection>
                                                {unitsError && (
                                                    <CellBlockSection>
                                                        <CellBlockValue>
                                                            {unitsError}
                                                        </CellBlockValue>
                                                    </CellBlockSection>
                                                )}
                                                <CellBlockFooter
                                                    isLoading={isMutationLoading}
                                                    disableSubmit={
                                                        !selectedUnitsHasChanged ||
                                                        selectedUnits.length === 0
                                                    }
                                                    hasChangesText={
                                                        selectedUnitsHasChanged
                                                            ? "Valet av enheter har osparade ändringar."
                                                            : undefined
                                                    }
                                                    onCancel={() => toggleEditMode(undefined)}
                                                    onSubmit={() => saveWithUnits()}
                                                />
                                            </>
                                        ) : (
                                            selectedUnits.map((unit) => (
                                                <CellBlockValue key={unit.id}>
                                                    {unit.name}
                                                </CellBlockValue>
                                            ))
                                        )}
                                    </CellBlock>
                                ) : (
                                    <Button
                                        outsideTitle="Lägg till enheter"
                                        shape="round"
                                        onClick={() => toggleEditMode("units")}
                                        icon={assets.plus}
                                    />
                                )}
                            </Cell>
                        )}
                    </Grid>
                )}
                {authUserData && !isCurrentUser && (
                    <Button
                        theme="inverted"
                        title="Ta bort"
                        disabled={isMutationLoading}
                        onClick={() => showDeletionConfirmModal()}
                        wrapperStyle={{
                            overflow: "hidden",
                            marginLeft: "auto",
                            marginTop: space.padding,
                        }}
                    />
                )}
            </Plate>
        </Container>
    );
};

export default EditAdministratorView;
